import React, { useMemo, useState, Fragment } from 'react';
import { Loan, Currency } from '../types';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { Trash2, Power, Landmark, Pencil, X, Calculator, Save, Edit2 } from 'lucide-react';
import { calculateMonthlyPayment, calculateTotalInterest } from '../utils/loans';
import { formatCurrency } from '../utils/currency';
import { CURRENCIES } from '../utils/countries';
interface LoanListProps {
  loans: Loan[];
  partnerNames?: [string, string];
  onDelete: (id: string) => void;
  onUpdate?: (id: string, updates: Partial<Omit<Loan, 'id' | 'createdAt'>>) => void;
  onToggleActive: (id: string) => void;
  onExport?: () => void;
}
{}
function LoanEditForm({
  loan,
  partnerNames,
  onSave,
  onCancel
}: {
  loan: Loan;
  partnerNames: [string, string];
  onSave: (id: string, updates: Partial<Omit<Loan, 'id' | 'createdAt'>>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(loan.name);
  const [person, setPerson] = useState(loan.person);
  const [totalAmount, setTotalAmount] = useState(loan.totalAmount.toString());
  const [depositAmount, setDepositAmount] = useState(loan.depositAmount.toString());
  const [currency, setCurrency] = useState<Currency>(loan.currency);
  const [interestRate, setInterestRate] = useState(loan.interestRate.toString());
  const [durationMonths, setDurationMonths] = useState(loan.durationMonths.toString());
  const [startYear, setStartYear] = useState(loan.startYear.toString());
  const [repaymentType, setRepaymentType] = useState<Loan['repaymentType']>(loan.repaymentType);
  const previewLoan = useMemo<Loan>(() => ({
    ...loan,
    name: name || loan.name,
    person,
    totalAmount: Number(totalAmount) || 0,
    depositAmount: Number(depositAmount) || 0,
    interestRate: Number(interestRate) || 0,
    durationMonths: Number(durationMonths) || 0,
    startYear: Number(startYear) || 0,
    currency,
    repaymentType
  }), [name, person, totalAmount, depositAmount, interestRate, durationMonths, startYear, currency, repaymentType, loan]);
  const monthlyPayment = calculateMonthlyPayment(previewLoan);
  const totalInterest = calculateTotalInterest(previewLoan);
  const principal = Math.max(0, previewLoan.totalAmount - previewLoan.depositAmount);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const totalVal = Number(totalAmount);
    const depositVal = Number(depositAmount);
    const rateVal = Number(interestRate);
    const durationVal = Number(durationMonths);
    const startYearVal = Number(startYear);
    if (name.trim() && !isNaN(totalVal) && !isNaN(depositVal) && !isNaN(rateVal) && !isNaN(durationVal) && !isNaN(startYearVal)) {
      onSave(loan.id, {
        name: name.trim(),
        person,
        totalAmount: totalVal,
        depositAmount: depositVal,
        interestRate: rateVal,
        durationMonths: durationVal,
        startYear: startYearVal,
        currency,
        repaymentType
      });
    }
  };
  return <tr className="block md:table-row border-b border-slate-100 last:border-0" data-id="element-966">
      <td colSpan={5} className="block md:table-cell p-0" data-id="element-967">
        <div className="bg-blue-50/50 border-y border-blue-200 px-4 py-5" data-id="element-968">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-969">
            <div className="flex items-center justify-between mb-2" data-id="element-970">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2" data-id="element-971">
                <Pencil className="w-4 h-4 text-blue-600" data-id="element-972" />
                Editing: {loan.name}
              </h4>
              <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-973">
                <X className="w-5 h-5" data-id="element-974" />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-975">
              <div className="col-span-2" data-id="element-976">
                <Label data-id="element-977">Loan Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Mortgage" required data-id="element-978" />
              </div>
              <div data-id="element-979">
                <Label data-id="element-980">Owner</Label>
                <Select value={person} onChange={e => setPerson(e.target.value)} options={[{
                value: 'Joint',
                label: 'Joint (50/50)'
              }, {
                value: partnerNames[0],
                label: partnerNames[0]
              }, {
                value: partnerNames[1],
                label: partnerNames[1]
              }]} data-id="element-981" />
              </div>
              <div data-id="element-982">
                <Label data-id="element-983">Currency</Label>
                <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.code} (${c.symbol})`
              }))} data-id="element-984" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-985">
              <div data-id="element-986">
                <Label data-id="element-987">Total Amount</Label>
                <Input type="number" min="0" step="1000" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} required data-id="element-988" />
              </div>
              <div data-id="element-989">
                <Label data-id="element-990">Down Payment / Deposit</Label>
                <Input type="number" min="0" step="1000" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} data-id="element-991" />
              </div>
              <div data-id="element-992">
                <Label data-id="element-993">Interest Rate (%)</Label>
                <Input type="number" min="0" step="0.01" value={interestRate} onChange={e => setInterestRate(e.target.value)} required data-id="element-994" />
              </div>
              <div data-id="element-995">
                <Label data-id="element-996">Repayment Type</Label>
                <Select value={repaymentType} onChange={e => setRepaymentType(e.target.value as Loan['repaymentType'])} options={[{
                value: 'annuity',
                label: 'Annuity (Fixed)'
              }, {
                value: 'linear',
                label: 'Linear (Decreasing)'
              }, {
                value: 'interest-only',
                label: 'Interest Only'
              }]} data-id="element-997" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-998">
              <div data-id="element-999">
                <Label data-id="element-1000">Duration (Months)</Label>
                <div className="relative" data-id="element-1001">
                  <Input type="number" min="1" step="1" value={durationMonths} onChange={e => setDurationMonths(e.target.value)} required data-id="element-1002" />
                  <span className="absolute right-3 top-2 text-xs text-slate-400 pointer-events-none" data-id="element-1003">
                    {durationMonths ? `${(Number(durationMonths) / 12).toFixed(1)} yrs` : ''}
                  </span>
                </div>
              </div>
              <div data-id="element-1004">
                <Label data-id="element-1005">Start Year (from now)</Label>
                <Input type="number" min="0" step="1" value={startYear} onChange={e => setStartYear(e.target.value)} required data-id="element-1006" />
              </div>
            </div>

            {/* Preview */}
            {principal > 0 && Number(durationMonths) > 0 && <div className="p-4 bg-white rounded-xl border border-slate-200 flex flex-col gap-3" data-id="element-1007">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-id="element-1008">
                  <Calculator className="w-4 h-4 text-blue-500" data-id="element-1009" />
                  Updated Summary
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-id="element-1010">
                  <div data-id="element-1011">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1012">Principal</p>
                    <p className="font-medium text-slate-900" data-id="element-1013">
                      {formatCurrency(principal, currency)}
                    </p>
                  </div>
                  <div data-id="element-1014">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1015">
                      Monthly Payment
                    </p>
                    <p className="font-bold text-blue-700" data-id="element-1016">
                      {formatCurrency(monthlyPayment, currency)}
                    </p>
                  </div>
                  <div data-id="element-1017">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1018">
                      Total Interest
                    </p>
                    <p className="font-medium text-rose-600" data-id="element-1019">
                      {formatCurrency(totalInterest, currency)}
                    </p>
                  </div>
                  <div data-id="element-1020">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1021">Total Cost</p>
                    <p className="font-medium text-slate-900" data-id="element-1022">
                      {formatCurrency(principal + totalInterest, currency)}
                    </p>
                  </div>
                </div>
              </div>}

            <div className="flex items-center gap-3 pt-2" data-id="element-1023">
              <Button type="submit" data-id="element-1024">
                <Save className="w-4 h-4 mr-2" data-id="element-1025" />
                Save Changes
              </Button>
              <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1026">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </td>
    </tr>;
}
function LoanRow({
  loan,
  partnerNames,
  onDelete,
  onEdit,
  onToggleActive
}: {
  loan: Loan;
  partnerNames: [string, string];
  onDelete: (id: string) => void;
  onEdit?: () => void;
  onToggleActive: (id: string) => void;
}) {
  const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
  const monthlyPayment = calculateMonthlyPayment(loan);
  const isActive = loan.isActive;
  const isFirstPartner = loan.person === partnerNames[0];
  const isJoint = loan.person === 'Joint';
  return <>
      {/* Mobile View */}
      <tr className={`md:hidden block border-b border-slate-100 p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1027">
        <td className="block p-0" data-id="element-1028">
          <div className="flex flex-col gap-3" data-id="element-1029">
            <div className="flex justify-between items-start gap-4" data-id="element-1030">
              <div className="flex flex-col" data-id="element-1031">
                <span className="font-medium text-slate-900 flex items-center gap-1.5" data-id="element-1032">
                  {loan.name}
                  {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1033">
                      INACTIVE
                    </span>}
                </span>
                <span className="text-xs text-slate-500 mt-0.5" data-id="element-1034">
                  {loan.interestRate}% · {loan.durationMonths} mos ·{' '}
                  {loan.repaymentType}
                </span>
              </div>
              <div className="flex flex-col items-end" data-id="element-1035">
                <span className="font-bold text-rose-600" data-id="element-1036">
                  -{formatCurrency(monthlyPayment, loan.currency)}/mo
                </span>
                <span className="text-xs font-medium text-slate-900 mt-0.5" data-id="element-1037">
                  Bal: {formatCurrency(principal, loan.currency)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-slate-50" data-id="element-1038">
              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-1039">
                {isJoint ? '🤝 Mutual' : loan.person}
              </span>
              <div className="flex items-center gap-1" data-id="element-1040">
                <Button variant="ghost" size="sm" onClick={() => onToggleActive(loan.id)} className="px-2 text-slate-400" data-id="element-1041">
                  <Power className="w-4 h-4" data-id="element-1042" />
                </Button>
                {onEdit && <Button variant="ghost" size="sm" onClick={onEdit} className="px-2 text-slate-400" data-id="element-1043">
                    <Edit2 className="w-4 h-4" data-id="element-1044" />
                  </Button>}
                <Button variant="ghost" size="sm" onClick={() => onDelete(loan.id)} className="px-2 text-slate-400" data-id="element-1045">
                  <Trash2 className="w-4 h-4" data-id="element-1046" />
                </Button>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Desktop View */}
      <tr className={`hidden md:table-row hover:bg-slate-50/50 transition-colors group border-b border-slate-100 last:border-0 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1047">
        <td className="px-4 py-4 font-medium text-slate-900" data-id="element-1048">
          <div className="flex flex-col" data-id="element-1049">
            <div className="flex items-center gap-2" data-id="element-1050">
              <span data-id="element-1051">{loan.name}</span>
              {!isActive && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1052">
                  INACTIVE
                </span>}
            </div>
            {loan.createdAt && <span className="text-xs text-slate-400 font-normal mt-0.5" data-id="element-1053">
                Added {new Date(loan.createdAt).toLocaleDateString()}
              </span>}
          </div>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-1054">
          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-1055">
            {isJoint ? '🤝 Mutual' : loan.person}
          </span>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-1056">
          <div className="flex flex-col" data-id="element-1057">
            <span className="font-medium text-slate-900" data-id="element-1058">
              {formatCurrency(principal, loan.currency)}
            </span>
            <span className="text-xs text-slate-400 mt-0.5" data-id="element-1059">
              {loan.interestRate}% · {loan.durationMonths} mos ·{' '}
              {loan.repaymentType}
              {loan.startYear > 0 ? ` · Starts Yr ${loan.startYear}` : ''}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-right" data-id="element-1060">
          <div className="flex flex-col items-end" data-id="element-1061">
            <span className="font-bold text-rose-600" data-id="element-1062">
              -{formatCurrency(monthlyPayment, loan.currency)}/mo
            </span>
            <span className="text-xs text-slate-400 mt-0.5" data-id="element-1063">
              Total Int:{' '}
              {formatCurrency(calculateTotalInterest(loan), loan.currency)}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-center" data-id="element-1064">
          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1065">
            <Button variant="ghost" size="sm" onClick={() => onToggleActive(loan.id)} className={`px-2 ${isActive ? 'text-slate-400 hover:text-amber-600' : 'text-amber-500 hover:text-emerald-600 !opacity-100'}`} title={isActive ? 'Deactivate' : 'Reactivate'} data-id="element-1066">
              <Power className="w-4 h-4" data-id="element-1067" />
            </Button>
            {onEdit && <Button variant="ghost" size="sm" onClick={onEdit} className="text-slate-400 hover:text-indigo-600 px-2" title="Edit" data-id="element-1068">
                <Edit2 className="w-4 h-4" data-id="element-1069" />
              </Button>}
            <Button variant="ghost" size="sm" onClick={() => onDelete(loan.id)} className="text-slate-400 hover:text-red-600 px-2" title="Delete" data-id="element-1070">
              <Trash2 className="w-4 h-4" data-id="element-1071" />
            </Button>
          </div>
        </td>
      </tr>
    </>;
}
export function LoanList({
  loans,
  partnerNames = ['Partner 1', 'Partner 2'],
  onDelete,
  onUpdate,
  onToggleActive,
  onExport
}: LoanListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  if (loans.length === 0) {
    return <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1072">
        <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]" data-id="element-1073">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3" data-id="element-1074">
            <Landmark className="w-6 h-6 text-slate-400" data-id="element-1075" />
          </div>
          <p data-id="element-1076">No loans added yet.</p>
          <p className="text-sm mt-1" data-id="element-1077">
            Add mortgages, car loans, or other debts to include their monthly
            payments in your projections.
          </p>
        </div>
      </div>;
  }
  return <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1078">
      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-slate-100" data-id="element-1079">
        {loans.map(loan => {
        const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
        const monthlyPayment = calculateMonthlyPayment(loan);
        const isActive = loan.isActive;
        const isFirstPartner = loan.person === partnerNames[0];
        const isJoint = loan.person === 'Joint';
        if (editingId === loan.id && onUpdate) {
          return <div key={loan.id} className="p-4" data-id="element-1080">
                <LoanEditForm loan={loan} partnerNames={partnerNames} onSave={(id, updates) => {
              onUpdate(id, updates);
              setEditingId(null);
            }} onCancel={() => setEditingId(null)} data-id="element-1081" />
              </div>;
        }
        return <div key={loan.id} className={`p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1082">
              <div className="flex items-start justify-between mb-2" data-id="element-1083">
                <div className="min-w-0 flex-1 mr-3" data-id="element-1084">
                  <div className="flex items-center gap-2" data-id="element-1085">
                    <p className="font-medium text-slate-900 truncate" data-id="element-1086">
                      {loan.name}
                    </p>
                    {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1087">
                        INACTIVE
                      </span>}
                  </div>
                  {loan.createdAt && <p className="text-xs text-slate-400 mt-0.5" data-id="element-1088">
                      Added {new Date(loan.createdAt).toLocaleDateString()}
                    </p>}
                </div>
                <div className="text-right shrink-0" data-id="element-1089">
                  <p className="font-bold text-rose-600" data-id="element-1090">
                    -{formatCurrency(monthlyPayment, loan.currency)}/mo
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-1091">
                    Int:{' '}
                    {formatCurrency(calculateTotalInterest(loan), loan.currency)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-3" data-id="element-1092">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-1093">
                  {isJoint ? '🤝 Mutual' : loan.person}
                </span>
                <span className="text-xs text-slate-500" data-id="element-1094">
                  {formatCurrency(principal, loan.currency)} ·{' '}
                  {loan.interestRate}% · {loan.durationMonths}mo ·{' '}
                  {loan.repaymentType}
                </span>
                {loan.startYear > 0 && <span className="text-xs text-blue-500" data-id="element-1095">
                    Starts Yr {loan.startYear}
                  </span>}
              </div>
              <div className="flex items-center justify-end gap-1" data-id="element-1096">
                <Button variant="ghost" size="sm" onClick={() => onToggleActive(loan.id)} className={`px-2 ${isActive ? 'text-slate-400' : 'text-amber-500'}`} data-id="element-1097">
                  <Power className="w-4 h-4" data-id="element-1098" />
                </Button>
                {onUpdate && <Button variant="ghost" size="sm" onClick={() => setEditingId(editingId === loan.id ? null : loan.id)} className="text-slate-400 px-2" data-id="element-1099">
                    <Edit2 className="w-4 h-4" data-id="element-1100" />
                  </Button>}
                <Button variant="ghost" size="sm" onClick={() => onDelete(loan.id)} className="text-slate-400 px-2" data-id="element-1101">
                  <Trash2 className="w-4 h-4" data-id="element-1102" />
                </Button>
              </div>
            </div>;
      })}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto" data-id="element-1103">
        <table className="w-full text-sm text-left" data-id="element-1104">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1105">
            <tr data-id="element-1106">
              <th className="px-4 py-3 font-medium" data-id="element-1107">Loan</th>
              <th className="px-4 py-3 font-medium" data-id="element-1108">Owner</th>
              <th className="px-4 py-3 font-medium" data-id="element-1109">Principal & Terms</th>
              <th className="px-4 py-3 font-medium text-right" data-id="element-1110">
                Monthly Payment
              </th>
              <th className="px-4 py-3 font-medium text-center" data-id="element-1111">Actions</th>
            </tr>
          </thead>
          <tbody data-id="element-1112">
            {loans.map(loan => <Fragment key={loan.id} data-id="element-1113">
                <LoanRow loan={loan} partnerNames={partnerNames} onDelete={onDelete} onEdit={onUpdate ? () => setEditingId(editingId === loan.id ? null : loan.id) : undefined} onToggleActive={onToggleActive} data-id="element-1114" />
                {editingId === loan.id && onUpdate && <LoanEditForm loan={loan} partnerNames={partnerNames} onSave={(id, updates) => {
              onUpdate(id, updates);
              setEditingId(null);
            }} onCancel={() => setEditingId(null)} data-id="element-1115" />}
              </Fragment>)}
          </tbody>
        </table>
      </div>
    </div>;
}